import { useCallback, useEffect, useMemo, useState } from 'react';
import { type SortingState } from '@tanstack/react-table';
import Input from '../common/Input';
import Modal from '../common/Modal';
import Table from '../common/Table';
import { formatBackendDateTimeToLocal } from '../lib/dateTime';
import { createInvoiceColumns } from './dashboardColumns';
import { Invoice } from '../types';
import { BulkEmailStats, useInvoicesQuery, useSendInvoiceEmailMutation, useUpdateInvoiceMutation } from '../hooks/useInvoices';

const invoiceStatusOptions = ['ALL', 'DRAFT', 'PAID', 'UNPAID', 'CANCELED'] as const;

type SingleEmailToast = {
  tone: 'success' | 'error';
  message: string;
};

function InvoicesTab() {
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [debouncedInvoiceSearchTerm, setDebouncedInvoiceSearchTerm] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('ALL');
  const [invoiceSorting, setInvoiceSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]);
  const [invoicesPageIndex, setInvoicesPageIndex] = useState(0);
  const [invoicesPageSize, setInvoicesPageSize] = useState(10);
  const [selectedInvoiceDescription, setSelectedInvoiceDescription] = useState<string | null>(null);
  const [sendingInvoiceId, setSendingInvoiceId] = useState<number | null>(null);
  const [invoiceActionMessage, setInvoiceActionMessage] = useState('');
  const [singleEmailToast, setSingleEmailToast] = useState<SingleEmailToast | null>(null);
  // const [bulkEmailMessage, setBulkEmailMessage] = useState('');
  const [bulkEmailStats, setBulkEmailStats] = useState<BulkEmailStats | null>(null);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const activeSort = invoiceSorting[0];
  const rawInvoicesSortField = activeSort?.id ? String(activeSort.id) : '';
  const invoicesSortField =
    rawInvoicesSortField === 'customerName'
      ? 'firstName'
      : rawInvoicesSortField === 'customerEmail'
        ? 'email'
        : rawInvoicesSortField;
  const invoicesSortDirection: 'asc' | 'desc' = activeSort?.desc ? 'desc' : 'asc';

  const invoicesQuery = useInvoicesQuery(
    true,
    invoicesPageIndex + 1,
    invoicesPageSize,
    invoiceStatusFilter,
    debouncedInvoiceSearchTerm,
    invoicesSortField,
    invoicesSortDirection
  );
  const sendInvoiceEmailMutation = useSendInvoiceEmailMutation();
  // const sendBulkEmailsMutation = useSendBulkEmailsMutation();
  const updateInvoiceMutation = useUpdateInvoiceMutation();

  const invoicesMeta = invoicesQuery.data?.meta;
  const hasActiveFilters = invoiceStatusFilter !== 'ALL' || invoiceSearchTerm.trim().length > 0;

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedInvoiceSearchTerm(invoiceSearchTerm);
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [invoiceSearchTerm]);

  useEffect(() => {
    if (!singleEmailToast) return;

    const timer = window.setTimeout(() => {
      setSingleEmailToast(null);
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [singleEmailToast]);

  // useEffect(() => {
  //   setInvoicesPageIndex(0);
  // }, [invoiceStatusFilter, debouncedInvoiceSearchTerm]);

  // useEffect(() => {
  //   setInvoicesPageIndex(0);
  // }, [invoicesSortField, invoicesSortDirection]);

  const sortedInvoices = useMemo<Invoice[]>(() => {
    return invoicesQuery.data?.data ?? [];
  }, [invoicesQuery.data?.data]);

  const currentPageInvoiceAmountTotal = useMemo(() => {
    return sortedInvoices.reduce((sum, invoice) => {
      const normalizedAmount = Number.parseFloat(invoice.amount.replace(/[^0-9.-]/g, ''));
      if (Number.isNaN(normalizedAmount)) return sum;
      return sum + normalizedAmount;
    }, 0);
  }, [sortedInvoices]);

  const formattedCurrentPageInvoiceAmountTotal = useMemo(
    () =>
      currentPageInvoiceAmountTotal.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [currentPageInvoiceAmountTotal]
  );

  const isEditChanged = useMemo(() => {
    if (!editingInvoice) return false;
    return editAmount.trim() !== editingInvoice.amount.trim() || editDescription.trim() !== editingInvoice.description.trim();
  }, [editAmount, editDescription, editingInvoice]);

  const handleSendInvoice = useCallback(
    async (invoiceId: number) => {
      setInvoiceActionMessage('');
      setSendingInvoiceId(invoiceId);
      try {
        await sendInvoiceEmailMutation.mutateAsync(invoiceId);
        setInvoiceActionMessage(`Invoice ${invoiceId} email sent successfully.`);
        setSingleEmailToast({
          tone: 'success',
          message: `Invoice ${invoiceId} email sent successfully.`,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to send invoice email.';
        setInvoiceActionMessage(message);
        setSingleEmailToast({
          tone: 'error',
          message,
        });
      } finally {
        setSendingInvoiceId(null);
      }
    },
    [sendInvoiceEmailMutation]
  );

  // const handleSendBulkEmails = useCallback(async () => {
  //   setBulkEmailMessage('');
  //   setBulkEmailStats(null);
  //   try {
  //     const result = await sendBulkEmailsMutation.mutateAsync();
  //     setBulkEmailStats(result);
  //     setBulkEmailMessage('Bulk invoice emails sent successfully.');
  //   } catch (error) {
  //     const message = error instanceof Error ? error.message : 'Failed to send bulk invoice emails.';
  //     setBulkEmailMessage(message);
  //   }
  // }, [sendBulkEmailsMutation]);

  const handleEditInvoice = useCallback((invoice: Invoice) => {
    setInvoiceActionMessage('');
    setEditingInvoice(invoice);
    setEditAmount(invoice.amount);
    setEditDescription(invoice.description);
  }, []);

  const handleClearFilters = useCallback(() => {
    setInvoiceSearchTerm('');
    setDebouncedInvoiceSearchTerm('');
    setInvoiceStatusFilter('ALL');
    setInvoicesPageIndex(0);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditingInvoice(null);
    setEditAmount('');
    setEditDescription('');
  }, []);

  const handleSaveInvoiceEdit = useCallback(async () => {
    if (!editingInvoice || !isEditChanged) return;

    setInvoiceActionMessage('');
    try {
      await updateInvoiceMutation.mutateAsync({
        invoiceId: editingInvoice.invoiceId,
        amount: editAmount.trim(),
        description: editDescription.trim(),
      });
      setInvoiceActionMessage(`Invoice ${editingInvoice.invoiceId} updated successfully.`);
      handleCloseEditModal();
      await invoicesQuery.refetch();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update invoice.';
      setInvoiceActionMessage(message);
    }
  }, [editAmount, editDescription, editingInvoice, handleCloseEditModal, invoicesQuery, isEditChanged, updateInvoiceMutation]);

  const invoiceColumns = useMemo(
    () =>
      createInvoiceColumns({
        sendingInvoiceId,
        isSendInvoicePending: sendInvoiceEmailMutation.isPending,
        onOpenDescription: setSelectedInvoiceDescription,
        onSendInvoice: (invoiceId: number) => void handleSendInvoice(invoiceId),
        onEditInvoice: handleEditInvoice,
      }),
    [handleEditInvoice, handleSendInvoice, sendInvoiceEmailMutation.isPending, sendingInvoiceId]
  );

  const invoiceErrorMessage = (invoicesQuery.error as Error | null)?.message ?? '';

  return (
    <>
      {singleEmailToast && (
        <div className="pointer-events-none fixed right-4 top-4 z-[1100]">
          <div
            className={`rounded-md border px-4 py-3 text-sm font-semibold shadow-lg ${
              singleEmailToast.tone === 'success'
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-red-200 bg-red-50 text-red-800'
            }`}
            role="status"
            aria-live="polite"
          >
            {singleEmailToast.message}
          </div>
        </div>
      )}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <Table<Invoice>
          data={sortedInvoices}
          columns={invoiceColumns}
          topHeader={
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex flex-col gap-3 sm:flex-1 sm:flex-row sm:items-end">
                <div className="max-w-lg flex-1 sm:min-w-0">
                  <Input
                    value={invoiceSearchTerm}
                    onChange={(event) => setInvoiceSearchTerm(event.target.value)}
                    placeholder="Search "
                    aria-label="Search invoices"
                  />
                </div>
                <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Status
                  <select
                    value={invoiceStatusFilter}
                    onChange={(event) => setInvoiceStatusFilter(event.target.value)}
                    className="min-w-[170px] rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                    aria-label="Filter invoices by status"
                  >
                    {invoiceStatusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </label>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
              {/* <button
                type="button"
                onClick={() => void handleSendBulkEmails()}
                disabled={sendBulkEmailsMutation.isPending || !hasDraftInvoices}
                className="flex items-center gap-2 rounded-md border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 transition hover:bg-indigo-100 disabled:pointer-events-none disabled:opacity-60 sm:ml-auto"
              >
                {sendBulkEmailsMutation.isPending && <Spinner size="sm" />}
                <span>{sendBulkEmailsMutation.isPending ? 'Sending...' : 'Send Bulk Emails'}</span>
              </button> */}
            </div>
          }
          loading={invoicesQuery.isFetching}
          enableSorting
          sorting={invoiceSorting}
          onSortingChange={setInvoiceSorting}
          pagination={{
            pageIndex: invoicesPageIndex,
            pageSize: invoicesPageSize,
            totalCount: invoicesMeta?.total ?? sortedInvoices.length,
            totalPages: invoicesMeta?.totalPages,
            onPageChange: setInvoicesPageIndex,
            onPageSizeChange: setInvoicesPageSize,
            pageSizeOptions: [10, 20, 40, 80],
          }}
        />
        <div className="mt-3 flex items-center justify-end rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-sm font-semibold text-slate-700">
            Total Amount (Current Page): <span className="text-slate-900">${formattedCurrentPageInvoiceAmountTotal}</span>
          </p>
        </div>

        {invoiceErrorMessage && <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{invoiceErrorMessage}</p>}
        {invoiceActionMessage && (
          <p className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-700">{invoiceActionMessage}</p>
        )}
        {/* {bulkEmailMessage && <p className="mt-4 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-700">{bulkEmailMessage}</p>} */}
      </div>

      <Modal isOpen={Boolean(selectedInvoiceDescription)} onClose={() => setSelectedInvoiceDescription(null)} title="Invoice Description">
        <p className="whitespace-pre-wrap text-sm text-slate-700">{selectedInvoiceDescription}</p>
      </Modal>

      <Modal isOpen={Boolean(bulkEmailStats)} onClose={() => setBulkEmailStats(null)} title="Bulk Email Summary">
        {bulkEmailStats && (
          <div className="space-y-3">
            <p className="text-sm text-slate-700">Bulk invoice emails finished successfully.</p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total Drafts</p>
                <p className="mt-1 text-lg font-bold text-slate-900">{bulkEmailStats.totalDrafts}</p>
              </div>
              <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-green-700">Sent</p>
                <p className="mt-1 text-lg font-bold text-green-800">{bulkEmailStats.sent}</p>
              </div>
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Skipped</p>
                <p className="mt-1 text-lg font-bold text-amber-800">{bulkEmailStats.skipped}</p>
              </div>
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-red-700">Failed</p>
                <p className="mt-1 text-lg font-bold text-red-800">{bulkEmailStats.failed}</p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={Boolean(editingInvoice)}
        onClose={handleCloseEditModal}
        title={editingInvoice ? `Edit Invoice #${editingInvoice.invoiceId}` : 'Edit Invoice'}
        subheading="Only amount and description can be updated."
        footer={
          <div className="flex w-full justify-end gap-2 px-4 py-2">
            <button
              type="button"
              onClick={handleCloseEditModal}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleSaveInvoiceEdit()}
              disabled={!isEditChanged || updateInvoiceMutation.isPending}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-indigo-300"
            >
              {updateInvoiceMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      >
        {editingInvoice && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Invoice ID</span>
              <input value={String(editingInvoice.invoiceId)} disabled className="rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-slate-600" />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">User ID</span>
              <input value={String(editingInvoice.userId)} disabled className="rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-slate-600" />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Name</span>
              <input value={editingInvoice.customerName} disabled className="rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-slate-600" />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Customer Email</span>
              <input value={editingInvoice.customerEmail} disabled className="rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-slate-600" />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Status</span>
              <input value={editingInvoice.status} disabled className="rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-slate-600" />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Created At</span>
              <input
                value={formatBackendDateTimeToLocal(editingInvoice.createdAt)}
                disabled
                className="rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-slate-600"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Transaction ID</span>
              <input value={editingInvoice.transactionId} disabled className="rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-slate-600" />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700">Transaction Status</span>
              <input value={editingInvoice.transactionStatus} disabled className="rounded-md border border-slate-300 bg-slate-100 px-3 py-2 text-slate-600" />
            </label>
            <label className="flex flex-col gap-1 text-sm sm:col-span-1">
              <span className="font-medium text-slate-700">Amount</span>
              <input
                value={editAmount}
                onChange={(event) => setEditAmount(event.target.value)}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm sm:col-span-2">
              <span className="font-medium text-slate-700">Description</span>
              <textarea
                value={editDescription}
                onChange={(event) => setEditDescription(event.target.value)}
                rows={4}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-700 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </label>
          </div>
        )}
      </Modal>
    </>
  );
}

export default InvoicesTab;
